import React, { useState } from 'react';
import { WarningCircle, CaretDown, CaretUp } from '@phosphor-icons/react';

/**
 * DataConfidenceBanner
 *
 * Surfaces the DACAMS pipeline provenance / audit trail. When the analysis had to fall
 * back to defaults or a stage failed, the result carries data_confidence != "high" and a
 * list of quality_flags / defaults_used. This banner makes that visible so the user knows
 * when a KPI (e.g. a RAC built on defaults) is ESTIMATED rather than validated.
 *
 * Renders nothing when the analysis is fully validated (data_confidence === "high") or
 * when provenance is absent (older analyses).
 */

interface QualityFlag {
  item: string;
  status: string;
  detail?: string;
}

interface DefaultUsed {
  param: string;
  value: unknown;
  reason?: string;
}

interface Provenance {
  methodology_version?: string;
  data_confidence?: 'high' | 'degraded' | 'low';
  quality_flags?: QualityFlag[];
  defaults_used?: DefaultUsed[];
}

interface Props {
  project?: { analysis_results?: { provenance?: Provenance } | null } | null;
}

const DataConfidenceBanner: React.FC<Props> = ({ project }) => {
  const [open, setOpen] = useState(false);

  const prov = project?.analysis_results?.provenance;
  const confidence = prov?.data_confidence;

  // Nothing to show when the analysis is fully validated (or provenance absent).
  if (!prov || !confidence || confidence === 'high') return null;

  const allFlags = prov.quality_flags ?? [];
  // A "computed" flag records a KPI built from REAL data (provenance/audit trail),
  // NOT a degradation — it must not be listed as a degraded KPI. Only these
  // statuses represent an actual fallback/degradation.
  const DEGRADED_STATUSES = new Set(['fallback_default', 'degraded', 'error', 'default']);
  const flags = allFlags.filter((f) => DEGRADED_STATUSES.has(f.status));
  const computedFlags = allFlags.filter((f) => f.status === 'computed');
  const defaults = prov.defaults_used ?? [];
  const isLow = confidence === 'low';

  const palette = isLow
    ? { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-800', icon: 'text-red-600' }
    : { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', icon: 'text-amber-600' };

  const title = isLow
    ? 'Análisis con datos incompletos — resultados no fiables'
    : 'Análisis degradado — algunos KPI son estimados (valores por defecto)';

  return (
    <div className={`p-4 rounded-xl border ${palette.bg} ${palette.border} mb-4`}>
      <div className="flex items-start gap-3">
        <WarningCircle size={22} weight="fill" className={`${palette.icon} mt-0.5 shrink-0`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm font-bold ${palette.text}`}>{title}</span>
            {(flags.length > 0 || defaults.length > 0 || computedFlags.length > 0) && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`flex items-center gap-1 text-xs font-semibold ${palette.text} hover:underline shrink-0`}
              >
                {open ? 'Ocultar detalle' : 'Ver detalle'}
                {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </button>
            )}
          </div>
          <p className={`text-xs mt-1 ${palette.text} opacity-80`}>
            Metodología v{prov.methodology_version ?? '—'} · confianza de datos:{' '}
            <span className="font-semibold uppercase">{confidence}</span>. Un KPI estimado se ha
            construido sobre valores por defecto, no sobre datos reales del proyecto.
          </p>

          {open && (
            <div className="mt-3 space-y-3">
              {flags.length > 0 && (
                <div>
                  <div className={`text-[11px] font-bold uppercase tracking-wider ${palette.text} mb-1`}>
                    KPI degradados ({flags.length})
                  </div>
                  <ul className="space-y-1">
                    {flags.map((f, i) => (
                      <li key={i} className={`text-xs ${palette.text}`}>
                        <span className="font-semibold">{f.item}</span>
                        <span className="opacity-60"> · {f.status}</span>
                        {f.detail ? <span className="opacity-80"> — {f.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {defaults.length > 0 && (
                <div>
                  <div className={`text-[11px] font-bold uppercase tracking-wider ${palette.text} mb-1`}>
                    Valores por defecto usados ({defaults.length})
                  </div>
                  <ul className="space-y-1">
                    {defaults.map((d, i) => (
                      <li key={i} className={`text-xs ${palette.text}`}>
                        <span className="font-semibold">{d.param}</span>
                        <span className="opacity-80"> = {String(d.value)}</span>
                        {d.reason ? <span className="opacity-60"> ({d.reason})</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Informational: KPIs computed from REAL data — NOT degraded, shown
                  separately (neutral) so they are not mistaken for fallbacks. */}
              {computedFlags.length > 0 && (
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider mb-1 text-[var(--text-secondary)]">
                    Calculado desde datos reales ({computedFlags.length})
                  </div>
                  <ul className="space-y-1">
                    {computedFlags.map((f, i) => (
                      <li key={i} className="text-xs text-[var(--text-secondary)]">
                        <span className="font-semibold">{f.item}</span>
                        {f.detail ? <span className="opacity-80"> — {f.detail}</span> : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataConfidenceBanner;
